import { FC, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, CircularProgress } from "@mui/material";

import StatusCheckedIcon from "@/assets/svg/interactions/icon-status-checked.svg?react";
import StatusUncheckedIcon from "@/assets/svg/interactions/icon-status-unchecked.svg?react";

import { useFetchChecklistStepStatuses } from "@/components/Procedures/API/ChecklistQueries";
import { RemoveStepCheckedModal } from "@/components/Procedures/RemoveStepCheckedModal";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeStepStatusTypes } from "@/model/Procedure/ProcedureNodeStepStatus";
import { StepStatus } from "@/model/Procedure/ProcedureNodeSteps";
import { UpdateStepStatusRequest } from "@/model/Procedure/UpdateStepStatusRequest";
import { UserContext } from "@/model/User/UserContext";
import {
	UserCompanyConfigurationKeys as ConfigKeys,
	GetUserCompanyConfigurationToggle,
} from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";
import { UserCompanyConfigurationContext } from "@/model/UserCompanyConfiguration/UserCompanyConfigurationContext";

import {
	checkAdminAccess,
	checkGroupAccess,
} from "@/service/ApplicationQueries";
import { useUpdateStepStatus } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

interface ChecklistStatusProps {
	checklistId: number | undefined;
	step: ProcedureNodeStep;
}

export const ChecklistStatus: FC<ChecklistStatusProps> = ({
	step,
	checklistId,
}) => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const [showRemoveCheckedStepModal, setShowRemoveCheckedStepModal] =
		useState(false);
	const updateProcedureDetailStepState = useUpdateStepStatus();
	const { isLoading, data: stepStatuses } = useFetchChecklistStepStatuses(
		checklistId,
		procedureId,
	);

	const user = useContext(UserContext);

	const CloseRemoveStepCheckedModal = () => {
		setShowRemoveCheckedStepModal(false);
	};

	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);

	const forceComment = () => {
		return GetUserCompanyConfigurationToggle(
			ConfigKeys.ForceComments,
			userCompanyConfiguration,
		);
	};

	const Save = async (readonly: boolean) => {
		const stepStatus = stepStatuses?.find((x) => x.Id === step.Id)!;
		if (readonly || step.Readonly || stepStatus.Disabled) {
			return;
		}

		let postValueToServer = false;
		let status: ProcedureNodeStepStatusTypes = 0;

		if (!stepStatus.Status) {
			status = 1;
			postValueToServer = true;
		} else if (stepStatus.Status) {
			if (forceComment()) {
				setShowRemoveCheckedStepModal(true);
			} else {
				postValueToServer = true;
			}
		}

		if (postValueToServer) {
			const request: UpdateStepStatusRequest = {
				ArchiveType: "Status",
				StepId: step.Id,
				ProcedureId: parseInt(id),
				SchemeId: checklistId,
				Status: status,
				SavedTagValue: undefined,
				Metadata: { ...step, Status: status },
			};

			await updateProcedureDetailStepState(request);
		}
	};

	if (
		step?.AccessLevel === "Locked" ||
		step.CheckpointType === CheckpointType.SATOSLive ||
		step.CheckpointType === CheckpointType.Info
	) {
		return <></>;
	}

	const getStepStatus = (
		step: ProcedureNodeStep,
		statuses: StepStatus[] | undefined,
	): StepStatus | undefined => {
		if (step && Array.isArray(statuses)) {
			// Ensure statuses is an array before calling find
			const intstatus = statuses.find((status) => status.Id === step.Id);

			if (intstatus) {
				intstatus.readonly = false;
			}

			if (intstatus && step?.GroupId > 0 && user) {
				// Check group and admin access
				if (
					!checkGroupAccess(user.Groups, [step?.GroupId]) &&
					!checkAdminAccess(user)
				) {
					intstatus.readonly = true;
					if (!intstatus.Status) {
						intstatus.Disabled = true;
					}
				}
			}
			return intstatus;
		}
		return undefined; // Return undefined when statuses is not an array or not available
	};

	const status = getStepStatus(step, stepStatuses);

	if (isLoading) {
		return <CircularProgress />;
	}

	if (
		status === undefined ||
		status.Disabled ||
		(step.CheckpointType === CheckpointType.ManualMeasurement && !status.Status)
	) {
		return (
			<Button
				variant="contained"
				color="error"
				disabled={true}
				type="button"
				sx={{
					padding: 1,
					minWidth: "40px",
					margin: 1,
				}}
			>
				<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
			</Button>
		);
	} else if (status.Status) {
		return (
			<>
				<Button
					variant="contained"
					color="success"
					disabled={step.IsSkipped}
					onClick={() => Save(status.readonly)}
					type="button"
					sx={{
						padding: 1,
						minWidth: "40px",
						margin: 1,
					}}
				>
					<StatusCheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
				</Button>
				<RemoveStepCheckedModal
					activeSchemeId={checklistId}
					show={showRemoveCheckedStepModal}
					step={step}
					CloseModal={CloseRemoveStepCheckedModal}
				/>
			</>
		);
	} else {
		return (
			<Button
				variant="contained"
				color="error"
				disabled={step.IsSkipped}
				onClick={() => Save(status.readonly)}
				type="button"
				sx={{
					padding: 1,
					minWidth: "40px",
					margin: 1,
				}}
			>
				<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
			</Button>
		);
	}
};
